/**
 * render.ts — canvas renderer for the Kitchen demo (v2: pickup/skip reframe).
 *
 * Adapted from reflex's renderer (DPR-aware resize, offscreen floor cache).
 * Domain-specific layers for the v2 regime:
 *
 *   - Items drawn as food EMOJI. The CURRENTLY-DECIDED item (the one under
 *     the agent right now) is ringed; decided items are faded out.
 *   - Value-tinted backgrounds: red for negative-in-ctx, green for positive
 *     flip items, so the viewer sees WHY the agent should pickup/skip.
 *   - Agent disc walks freely (BFS) between items; no visited mask, no
 *     percept window (those were v1's navigation-era artifacts).
 *   - Context badge in the corner (morning/evening). The time-of-day flips
 *     item values.
 *   - Pickup/skip flash: the last decision's action is shown next to the
 *     basket so the viewer can see the binary call being made.
 */
import {
  GRID, EMPTY, ITEM_EMOJI, ITEM_VALUE,
  N_CTX, CTX_NAMES,
  type ThemeName,
} from './constants';
import type { KitchenEnv } from './environment';

const FLOOR = '#0b1a24';
const GRID_LINE = 'rgba(255,255,255,0.04)';
const CURRENT_RING = 'rgba(205, 232, 255, 0.85)';
const PICKED_UP_BORDER = 'rgba(85, 168, 104, 0.95)'; // green — picked into basket
// Dashed blue border on duplicate encounters (type already collected → the
// legal mask forced SKIP). Visually distinct from picked-up (solid green)
// and from first-time skips (plain dark tint) so the viewer can see the
// constraint being enforced, not infer it.
const DUPLICATE_BORDER = 'rgba(76, 114, 176, 0.7)';
const DECIDED_TINT = 'rgba(0, 0, 0, 0.45)';
const AGENT_BODY = '#cde8ff';
const AGENT_RING = '#4c72b0';
const BADGE_MORNING = '#e0b34a';
const BADGE_EVENING = '#7a55ae';

const EASE = 0.3;

export interface AgentAnim {
  r: number;
  c: number;
  sr: number; // smoothed r for animation
  sc: number; // smoothed c
  prevR: number; // position before this step's BFS walk (for the trail)
  prevC: number;
}

export function makeAgentAnim(r: number, c: number): AgentAnim {
  return { r, c, sr: r, sc: c, prevR: r, prevC: c };
}

/** Smoothly interpolate (sr,sc) → (r,c). Call once per rAF. */
export function easeAgentAnim(a: AgentAnim): void {
  a.sr += (a.r - a.sr) * EASE;
  a.sc += (a.c - a.sc) * EASE;
}

export interface RenderState {
  env: KitchenEnv;
  agentAnim: AgentAnim;
  theme: ThemeName;
  currentCtx: number;
  episodeSteps: number;
  episodeReturn: number;
  episodeBasket: number;
  episodeAcc: number;
  totalSteps: number;
  arm: string;
  phase: string;
  lastReason: string;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private bg: HTMLCanvasElement;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas not supported');
    this.ctx = ctx;
    this.bg = document.createElement('canvas');
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w === 0 || h === 0) return;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.renderBackground();
  }

  get widthPx(): number {
    return this.canvas.getBoundingClientRect().width;
  }
  get heightPx(): number {
    return this.canvas.getBoundingClientRect().height;
  }

  private cellPx(): number {
    return Math.min(this.widthPx / GRID, this.heightPx / GRID);
  }

  private origin(): { ox: number; oy: number; cell: number } {
    const cell = this.cellPx();
    const ox = (this.widthPx - cell * GRID) / 2;
    const oy = (this.heightPx - cell * GRID) / 2;
    return { ox, oy, cell };
  }

  private renderBackground(): void {
    const w = this.widthPx;
    const h = this.heightPx;
    this.bg.width = Math.floor(w);
    this.bg.height = Math.floor(h);
    const ctx = this.bg.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = FLOOR;
    ctx.fillRect(0, 0, w, h);
  }

  frame(_t: number, state: RenderState): void {
    const w = this.widthPx;
    const h = this.heightPx;
    if (w === 0 || h === 0 || this.bg.width === 0) {
      this.resize();
      return;
    }
    easeAgentAnim(state.agentAnim);
    const ctx = this.ctx;
    const { ox, oy, cell } = this.origin();
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(this.bg, 0, 0);

    // Grid lines.
    ctx.strokeStyle = GRID_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= GRID; i++) {
      ctx.moveTo(ox + i * cell, oy);
      ctx.lineTo(ox + i * cell, oy + GRID * cell);
      ctx.moveTo(ox, oy + i * cell);
      ctx.lineTo(ox + GRID * cell, oy + i * cell);
    }
    ctx.stroke();

    // Items as emoji with value-tint backgrounds; decided items faded.
    ctx.font = `${Math.floor(cell * 0.7)}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const curR = state.env.r;
    const curC = state.env.c;
    for (let r = 0; r < GRID; r++) {
      for (let c = 0; c < GRID; c++) {
        const cellV = state.env.truth[r * GRID + c];
        if (cellV === EMPTY) continue;
        const cx = ox + c * cell + cell / 2;
        const cy = oy + r * cell + cell / 2;
        const val = ITEM_VALUE[cellV][state.env.ctx];
        if (val < 0) {
          ctx.fillStyle = 'rgba(196, 78, 82, 0.20)';
          ctx.fillRect(ox + c * cell + 1, oy + r * cell + 1, cell - 2, cell - 2);
        } else if (val > 1) {
          ctx.fillStyle = 'rgba(85, 168, 104, 0.18)';
          ctx.fillRect(ox + c * cell + 1, oy + r * cell + 1, cell - 2, cell - 2);
        }
        // Fade decided items so the viewer sees what's still on the table.
        if (state.env.decided[r * GRID + c]) {
          ctx.fillStyle = DECIDED_TINT;
          ctx.fillRect(ox + c * cell + 1, oy + r * cell + 1, cell - 2, cell - 2);
        }
        ctx.fillText(ITEM_EMOJI[cellV], cx, cy);
        // Green border on picked-up items — distinguishes them from the
        // dark-faded skipped items. Drawn after the emoji so it sits on top.
        if (state.env.pickedUp[r * GRID + c]) {
          ctx.strokeStyle = PICKED_UP_BORDER;
          ctx.lineWidth = 2;
          ctx.strokeRect(
            ox + c * cell + 1.5, oy + r * cell + 1.5, cell - 3, cell - 3,
          );
        } else if (state.env.duplicateDecided[r * GRID + c]) {
          // Dashed blue border on duplicate encounters (type already in the
          // basket → PICKUP was illegal, agent forced to SKIP). Distinct
          // from picked-up (solid green) and from first-time skips (no
          // border). Makes the constraint visible on the grid.
          ctx.strokeStyle = DUPLICATE_BORDER;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([3, 3]);
          ctx.strokeRect(
            ox + c * cell + 1.5, oy + r * cell + 1.5, cell - 3, cell - 3,
          );
          ctx.setLineDash([]);
        }
      }
    }

    // Ring the CURRENTLY-DECIDED item (the one under the agent right now).
    if (state.env.currentItem >= 0) {
      ctx.strokeStyle = CURRENT_RING;
      ctx.lineWidth = 2;
      ctx.strokeRect(
        ox + curC * cell + 1, oy + curR * cell + 1, cell - 2, cell - 2,
      );
    }

    // Agent disc (use eased position for smooth motion).
    const ax = ox + state.agentAnim.sc * cell + cell / 2;
    const ay = oy + state.agentAnim.sr * cell + cell / 2;
    ctx.fillStyle = AGENT_BODY;
    ctx.strokeStyle = AGENT_RING;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(ax, ay, cell * 0.32, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Overlay: context badge only. The basket / role / last-action / acc
    // now live in the sidebar (under the Run block) so they don't clutter
    // the canvas. The context badge stays because it's a WORLD property —
    // the viewer needs it to read the item value-tints at a glance. It
    // lives in the BOTTOM-left so it doesn't collide with the (?) help
    // button (fixed top-left) or the canvas's top edge.
    ctx.font = '14px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    const ctxName = state.currentCtx < N_CTX ? CTX_NAMES[state.currentCtx] : '?';
    ctx.fillStyle = state.currentCtx === 0 ? BADGE_MORNING : BADGE_EVENING;
    ctx.fillText(`◐ ${ctxName}`, 14, h - 14);
  }
}
